import React from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import portfolioData from "../data/portfolio.json";

interface Project {
  id: string;
  title: string;
  description: string;
  image: string;
  technologies: string[];
  implementation: string;
  challenges: string;
  results: string;
  type: string;
  url: string;
}

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const project = portfolioData.find((p) => p.id === id) as Project | undefined;

  if (!project) {
    return (
      <Box
        sx={{
          py: 8,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor:
            theme.palette.mode === "dark" ? "background.default" : "grey.50",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" align="center" gutterBottom sx={{ fontSize: "2rem" }}>
            Projekt nicht gefunden
          </Typography>
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button
              variant="contained"
              onClick={() => navigate("/portfolio")}
              startIcon={<ArrowBackIcon />}
            >
              Zurück zur Übersicht
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor:
          theme.palette.mode === "dark" ? "background.default" : "grey.50",
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Button
            component={RouterLink}
            to="/portfolio"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              mb: 4,
              borderRadius: "50px",
              px: 3,
              py: 1,
              borderWidth: 2,
              "&:hover": {
                borderWidth: 2,
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease-in-out",
            }}
          >
            Zurück zum Portfolio
          </Button>

          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: 0,
                    paddingTop: "75%",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 8px 32px rgba(0,0,0,0.3)"
                        : "0 8px 32px rgba(0,0,0,0.1)",
                  }}
                >
                  <Box
                    component="img"
                    src={project.image}
                    alt={project.title}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", md: "3rem" },
                    fontWeight: 700,
                    mb: 2,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {project.title}
                </Typography>

                <Box sx={{ mb: 4 }}>
                  {project.technologies.map((tech) => (
                    <Chip
                      key={tech}
                      label={tech}
                      sx={{
                        mr: 1,
                        mb: 1,
                        borderColor: "primary.main",
                        color: "primary.main",
                      }}
                      variant="outlined"
                    />
                  ))}
                </Box>

                <Typography variant="body1" paragraph>
                  {project.description}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 2,
                    borderRadius: "50px",
                    px: 4,
                    py: 2,
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease-in-out",
                  }}
                >
                  Projekt ansehen
                </Button>
              </motion.div>
            </Grid>
          </Grid>

          <Grid container spacing={4} sx={{ mt: 6 }}>
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: "primary.main",
                  }}
                >
                  Implementierung
                </Typography>
                <Typography variant="body1" paragraph>
                  {project.implementation}
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: "primary.main",
                  }}
                >
                  Herausforderungen
                </Typography>
                <Typography variant="body1" paragraph>
                  {project.challenges}
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1 }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: "primary.main",
                  }}
                >
                  Ergebnisse
                </Typography>
                <Typography variant="body1" paragraph>
                  {project.results}
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ProjectDetail;
